import { useState, useEffect } from 'react'
import { Activity, Cpu, HardDrive, Database } from 'lucide-react'

interface SystemHealthData {
  status: 'online' | 'warning' | 'critical'
  uptime: string
  cpu: number
  memory: number
  storage: number
  database: 'connected' | 'disconnected'
  lastCheck: string
}

export default function SystemHealthCard() {
  const [health, setHealth] = useState<SystemHealthData>({
    status: 'online',
    uptime: '99.9%',
    cpu: 23,
    memory: 45,
    storage: 67,
    database: 'connected',
    lastCheck: new Date().toISOString()
  })

  useEffect(() => {
    // TODO: Implement real system health monitoring
    const interval = setInterval(() => {
      setHealth(prev => ({
        ...prev,
        lastCheck: new Date().toISOString()
      }))
    }, 30000)

    return () => clearInterval(interval)
  }, [])

  const getStatusColor = (status: SystemHealthData['status']) => {
    switch (status) {
      case 'online': return '#00FF00'
      case 'warning': return '#FFA500'
      case 'critical': return '#FF0000'
    }
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1A1F2E, #242938)',
      border: '2px solid #00FF00',
      borderRadius: '12px',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 0 20px rgba(0, 255, 0, 0.2)',
      height: '100%'
    }}>
      {/* Corner accent */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100px',
        height: '100px',
        background: 'linear-gradient(135deg, transparent 50%, rgba(0, 255, 0, 0.2) 50%)',
        pointerEvents: 'none'
      }} />

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <div style={{
          background: 'rgba(0, 255, 0, 0.2)',
          border: '2px solid #00FF00',
          borderRadius: '8px',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Activity style={{ width: 24, height: 24, color: '#00FF00' }} />
        </div>
        <h3 style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '20px',
          fontWeight: 700,
          color: '#00FF00',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          margin: 0,
          textShadow: '0 0 10px rgba(0, 255, 0, 0.6)'
        }}>
          System Health
        </h3>
      </div>

      {/* Status */}
      <div style={{
        background: `rgba(${health.status === 'online' ? '0, 255, 0' : health.status === 'warning' ? '255, 165, 0' : '255, 0, 0'}, 0.1)`,
        border: `1px solid ${getStatusColor(health.status)}`,
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '4px'
          }}>
            Status
          </div>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: getStatusColor(health.status),
            fontFamily: 'Rajdhani, sans-serif',
            textTransform: 'uppercase'
          }}>
            {health.status}
          </div>
        </div>
        <div style={{
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: getStatusColor(health.status),
          boxShadow: `0 0 15px ${getStatusColor(health.status)}`,
          animation: 'pulse 2s infinite'
        }} />
      </div>

      {/* Metrics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{
          background: 'rgba(0, 240, 255, 0.1)',
          border: '1px solid rgba(0, 240, 255, 0.3)',
          borderRadius: '6px',
          padding: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Cpu style={{ width: 16, height: 16, color: '#00F0FF' }} />
            <div style={{
              fontSize: '11px',
              color: '#8A95AA',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontWeight: 600
            }}>
              CPU
            </div>
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#00F0FF',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {health.cpu}%
          </div>
        </div>

        <div style={{
          background: 'rgba(255, 107, 53, 0.1)',
          border: '1px solid rgba(255, 107, 53, 0.3)',
          borderRadius: '6px',
          padding: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Database style={{ width: 16, height: 16, color: '#FF6B35' }} />
            <div style={{
              fontSize: '11px',
              color: '#8A95AA',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontWeight: 600
            }}>
              Memory
            </div>
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#FF6B35',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {health.memory}%
          </div>
        </div>

        <div style={{
          background: 'rgba(255, 217, 61, 0.1)',
          border: '1px solid rgba(255, 217, 61, 0.3)',
          borderRadius: '6px',
          padding: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <HardDrive style={{ width: 16, height: 16, color: '#FFD93D' }} />
            <div style={{
              fontSize: '11px',
              color: '#8A95AA',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontWeight: 600
            }}>
              Storage
            </div>
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#FFD93D',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {health.storage}%
          </div>
        </div>

        <div style={{
          background: 'rgba(107, 207, 127, 0.1)',
          border: '1px solid rgba(107, 207, 127, 0.3)',
          borderRadius: '6px',
          padding: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Database style={{ width: 16, height: 16, color: '#6BCF7F' }} />
            <div style={{
              fontSize: '11px',
              color: '#8A95AA',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontWeight: 600
            }}>
              Database
            </div>
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            color: '#6BCF7F',
            fontFamily: 'Rajdhani, sans-serif',
            textTransform: 'uppercase'
          }}>
            {health.database}
          </div>
        </div>
      </div>

      {/* Uptime */}
      <div style={{
        background: 'rgba(0, 255, 0, 0.05)',
        border: '1px solid rgba(0, 255, 0, 0.2)',
        borderRadius: '6px',
        padding: '12px',
        marginBottom: '12px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '11px',
          color: '#8A95AA',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          fontWeight: 600,
          marginBottom: '4px'
        }}>
          Uptime
        </div>
        <div style={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#00FF00',
          fontFamily: 'Rajdhani, sans-serif'
        }}>
          {health.uptime}
        </div>
      </div>

      {/* Last Check */}
      <div style={{
        fontSize: '11px',
        color: '#8A95AA',
        textAlign: 'center'
      }}>
        Last Check: {new Date(health.lastCheck).toLocaleString()}
      </div>
    </div>
  )
}
